import { isMissing, reportField, reportFieldValue } from './reportFields';

// Rows: one per trial - unless the layout has diary (task) fields, then one per diary entry, with
// the trial's own fields repeated. To tell those rows apart, VA No and Task are always included
// then, and the diary columns come right after Trial No (where the report and Excel show them
// without scrolling; Excel also freezes them).
const DIARY_ID_KEYS = ['diaries.vano', 'diaries.task'];
const isDiaryField = (key) => key.startsWith('diaries.');

/**
 * The column keys of the report for a layout's field keys, see above.
 * @returns {{ keys: string[], perDiaryEntry: boolean, addedKeys: string[], frozenColumns: number }}
 */
export function reportColumnKeys(fields) {
  const diary = fields.filter(isDiaryField);
  if (diary.length === 0) return { keys: fields, perDiaryEntry: false, addedKeys: [], frozenColumns: 0 };

  const diaryColumns = [...DIARY_ID_KEYS, ...diary.filter((k) => !DIARY_ID_KEYS.includes(k))];
  const others = fields.filter((k) => !isDiaryField(k));
  const at = others.indexOf('trialno') + 1; // 0 (at the start) without Trial No
  return {
    keys: [...others.slice(0, at), ...diaryColumns, ...others.slice(at)],
    perDiaryEntry: true,
    addedKeys: DIARY_ID_KEYS.filter((k) => !diary.includes(k)),
    frozenColumns: at + DIARY_ID_KEYS.length,
  };
}

/**
 * Expands a row whose diary fields hold arrays into one row per diary entry, repeating the other
 * fields. A trial without diary entries still gives one row (with the diary fields empty).
 */
function expand(row) {
  const arrayIndices = row.flatMap((value, i) => (Array.isArray(value) ? [i] : []));
  if (arrayIndices.length === 0) return [row];

  const count = Math.max(1, ...arrayIndices.map((i) => row[i].length));
  return Array.from({ length: count }, (_, n) => {
    const expanded = [...row];
    arrayIndices.forEach((i) => { expanded[i] = row[i][n] ?? null; });
    return expanded;
  });
}

// A diary entry with none of these filled in is an empty task row; hidden by default (a trial whose
// entries are all empty still gets its one row)
const TASK_KEYS = ['vano', 'task', 'timeinterval', 'timeinterval_text', 'planned_interval', 'planned_date', 'bbch', 'persons', 'donedate'];
const isEmptyEntry = (entry) => TASK_KEYS.every((key) => isMissing(entry?.[key]));

/**
 * The report for a layout's field keys: { columns: field definitions (see reportFields), header:
 * their labels, rows: raw values (null when missing), rowTrials: each row's trial index, rowEntries:
 * each row's diary entry (null without diary fields, or for a trial without entries), trialNos:
 * each trial's number, perDiaryEntry, addedKeys, frozenColumns, trialCount, hiddenRows: empty task
 * rows left out }. Formatting is left to the table and the Excel export (formatReportValue), so both
 * show the same.
 */
export default function ReportGenerator({ trials, fields, hideEmptyTaskRows = false }) {
  const { keys, perDiaryEntry, addedKeys, frozenColumns } = reportColumnKeys(fields);
  const columns = keys.map(reportField);
  const rows = [];
  const rowTrials = []; // per row, the index of its trial (rows of one trial are consecutive)
  const rowEntries = [];
  let hiddenRows = 0;
  trials.forEach((trial, t) => {
    let source = trial;
    if (perDiaryEntry && hideEmptyTaskRows && Array.isArray(trial.diaries)) {
      const kept = trial.diaries.filter((entry) => !isEmptyEntry(entry));
      // A trial with only empty entries keeps one row, with the diary fields empty
      hiddenRows += trial.diaries.length - Math.max(kept.length, trial.diaries.length ? 1 : 0);
      source = { ...trial, diaries: kept };
    }
    // With diary fields, the n-th row of a trial is its n-th (kept) entry
    expand(columns.map((def) => reportFieldValue(def, source) ?? null)).forEach((row, n) => {
      rows.push(row);
      rowTrials.push(t);
      rowEntries.push(perDiaryEntry && Array.isArray(source.diaries) ? source.diaries[n] ?? null : null);
    });
  });

  return {
    columns,
    header: columns.map((c) => c.label),
    rows,
    rowTrials,
    rowEntries,
    trialNos: trials.map((trial) => trial.trialno ?? ''),
    perDiaryEntry,
    addedKeys,
    frozenColumns,
    trialCount: trials.length,
    hiddenRows,
  };
}